/**
 * Geolocalización centralizada — funciona en:
 *  • Navegador web / Chrome móvil (navigator.geolocation).
 *  • Safari en iPhone (navigator.geolocation + mensajes adaptados).
 *  • Android APK / Capacitor (window.Capacitor.Plugins.Geolocation cuando
 *    está disponible — sin necesidad de añadir la dependencia al bundle web).
 *
 * Devuelve siempre: { lat: number, lng: number, source: 'native' | 'web' }.
 * En error lanza un Error con mensaje en español listo para mostrar al usuario.
 */

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad moderno se reporta como Mac con touch.
  const iPadOSAsMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOSAsMac;
};

const isSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
};

const isSecureContext = () => {
  if (typeof window === "undefined") return false;
  // window.isSecureContext considera localhost también seguro.
  if (typeof window.isSecureContext === "boolean") return window.isSecureContext;
  return window.location?.protocol === "https:";
};

/** Indica si la app corre dentro de Capacitor (APK/iOS nativo). */
export const isCapacitorNative = () => {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
  return !!cap.isNative;
};

const friendlyWebError = (err) => {
  // PositionError.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT.
  const code = err?.code;
  if (code === 1) {
    if (isIos() && isSafari()) {
      return "Permiso de ubicación denegado. Actívalo desde Ajustes › Safari › Ubicación en tu iPhone.";
    }
    return "Permiso de ubicación denegado. Habilítalo desde la configuración del navegador.";
  }
  if (code === 2) return "Ubicación desactivada. Activa el GPS y vuelve a intentarlo.";
  if (code === 3) return "Tiempo de espera agotado al obtener la ubicación. Inténtalo de nuevo.";
  return "Error al obtener la ubicación.";
};

const getViaCapacitor = async () => {
  const Geo = window.Capacitor?.Plugins?.Geolocation;
  if (!Geo) throw new Error("Plugin nativo de geolocalización no disponible.");
  // Pide permisos primero — comportamiento estándar de @capacitor/geolocation.
  try {
    if (typeof Geo.requestPermissions === "function") {
      const perm = await Geo.requestPermissions();
      const granted = perm?.location === "granted" || perm?.coarseLocation === "granted";
      if (!granted) throw new Error("Permiso de ubicación denegado.");
    }
  } catch (e) {
    throw new Error(e?.message || "Permiso de ubicación denegado.");
  }
  const pos = await Geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 });
  return {
    lat: pos?.coords?.latitude,
    lng: pos?.coords?.longitude,
    source: "native",
  };
};

const getViaWeb = () => new Promise((resolve, reject) => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return reject(new Error("Este navegador no soporta geolocalización."));
  }
  if (!isSecureContext()) {
    return reject(new Error("La geolocalización requiere HTTPS. Abre la app desde una URL segura."));
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, source: "web" }),
    (err) => reject(new Error(friendlyWebError(err))),
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
  );
});

/**
 * Punto único de entrada para obtener la ubicación actual.
 * Detecta automáticamente el entorno y aplica la estrategia adecuada.
 */
export async function getCurrentLocation() {
  if (isCapacitorNative()) {
    return getViaCapacitor();
  }
  return getViaWeb();
}
